import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, EntityState } from "@reduxjs/toolkit"
import { AppThunkAPIType } from "../../app/appTypes"
import { RootState } from "../../app/store"
import { getWithAuth } from "../../http"
import { logout } from "../user/userSlice"

export interface UserSetting {
    featureFlags: FeatureFlag[]
    companySettings: CompanySetting[]
}

export interface FeatureFlag {
    id: string
    code: string
    description: string
}

export interface CompanySetting {
    id: string
    type: string
    code: string
    value: string
    isActive: boolean
}

export const sessionDurationCode = 'SESSION_DURATION'
export const documentTypeConfigurationFeatureFlag = 'DocumentTypeConfiguration'
export const departmentConfigurationFeatureFlag = 'DepartmentConfiguration'
export const dynamicUrsFeatureFlag = 'DynamicUrs'
export const documentSysUpdateFeatureFlag = 'DocumentSysUpdate'
export const catalogRequirementFeatureFlag = 'CatalogRequierment'
    
const adapter = createEntityAdapter<CompanySetting>({
    selectId: c => c.code,
})

const featureFlagsAdapter = createEntityAdapter<FeatureFlag>({
    selectId: f => f.code,
})

export type Locale = "en-US" | "pl" | "de"

export interface SettingsState {
    locale: Locale
    leftMenuOpen: boolean
    companySettings: EntityState<CompanySetting> & { loaded: boolean }
    featureFlags: EntityState<FeatureFlag> & { loaded: boolean }
}

export const loadCompanySettings = createAsyncThunk<UserSetting, void, AppThunkAPIType>('settings/load-company-data', async (_, api) => {
    const { dispatch, rejectWithValue } = api
    const result = await dispatch(getWithAuth({ url: "api/user-settings/company" }))
    const { payload } = result
    if (getWithAuth.fulfilled.match(result)) {
        return payload as UserSetting
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})

export const setLocalePersistent = createAsyncThunk<Locale, Locale, AppThunkAPIType>(
    "settings/setLocalePersistent",
    async (locale) => {
        localStorage.setItem("Settings_Locale", locale)
        return locale
    })

export const setActivityTimeoutPersistent = createAsyncThunk<number, number, AppThunkAPIType>(
    "settings/setActivityTimeoutPersistent",
    async (activityTimeout) => {
        localStorage.setItem("Settings_ActivityTimeout", activityTimeout.toString())
        return activityTimeout
    })

export const settingsSlice = createSlice({
    name: 'settings',
    initialState: (): SettingsState => {
        return {
            locale: (localStorage.getItem("Settings_Locale") ?? "pl") as Locale,
            leftMenuOpen: false,
            companySettings: adapter.getInitialState({
                loaded: false
            }),
            featureFlags: featureFlagsAdapter.getInitialState({
                loaded: false
            })
        }
    },
    reducers: {
        setLocale: (state, action) => {
            state.locale = action.payload
        },
        setLeftMenuOpen: (state, action) => {
            state.leftMenuOpen = action.payload
        },
    },
    extraReducers: builder => {
        builder.addCase(setLocalePersistent.fulfilled, (state, action) => {
            state.locale = action.payload
        })
        builder.addCase(loadCompanySettings.fulfilled, (state, action) => {
            adapter.setAll(state.companySettings, action.payload.companySettings)
            state.companySettings.loaded = true
            featureFlagsAdapter.setAll(state.featureFlags, action.payload.featureFlags)
            state.featureFlags.loaded = true
        })
        builder.addCase(logout.fulfilled, (state, action) => {
            adapter.removeAll(state.companySettings)
            state.companySettings.loaded = false
            featureFlagsAdapter.removeAll(state.featureFlags)
            state.featureFlags.loaded = false
        })
    }
})

export const { setLocale, setLeftMenuOpen } = settingsSlice.actions

export const selectSettings = (state: RootState) => state.settings

export const { selectById: selectSettingByCode } = adapter.getSelectors()
export const { selectById: selectFeatureFlagByCode, selectAll: selectAllFeatureFlags } = featureFlagsAdapter.getSelectors()

export const selectActivityTimeout = createSelector(
    (state: RootState) => selectSettings(state),
    (settings) => {
        const sd = selectSettingByCode(settings.companySettings, sessionDurationCode)
        if (sd) {
            return parseInt(sd.value) * 60 * 1000
        } else {
            return null
        }
    },
)

export const selectCompanyFeatureFlags = createSelector(
    (state: RootState) => selectSettings(state),
    (settings) => selectAllFeatureFlags(settings.featureFlags),
)

export const selectHasFeatureFlag = (featureFlagCode: string) => createSelector(
    (state: RootState) => selectSettings(state),
    (settings) => !!selectFeatureFlagByCode(settings.featureFlags, featureFlagCode),
)

export default settingsSlice.reducer
